import { useEffect, useState, type ReactNode } from 'react';
import { APP_STAGE, APP_VERSION } from '@jarvis/shared/constants';
import { google, useGoogle } from '../lib/google';
import { useApiStatus } from '../lib/health';
import { prefsStore, STATE_MODE_LABEL, STATE_MODES, useIsDark, usePrefs, type StateMode } from '../lib/prefs';
import { Ambient } from './Ambient';
import { Backdrop, Frame } from './hud/Stage';
import { Clock } from './hud/widgets';
import { Icon, type IconName } from './Icon';

export interface NavItem { id: string; label: string; icon: IconName; bottom?: boolean }

function Sidebar({ items, current }: { items: NavItem[]; current: string }) {
  const api = useApiStatus();
  const gs = useGoogle();
  useEffect(() => { if (api.state === 'up') void google.load(); }, [api.state]);
  const gmailOn = Boolean(gs?.connected && !gs.needsReconnect);
  const link = (v: NavItem) => (
    <a key={v.id} href={`#/${v.id}`} aria-current={v.id === current ? 'page' : undefined}><Icon name={v.icon} /><span>{v.label}</span></a>
  );
  const conn = api.state === 'up' ? api.health.connections : null;
  return (
    <aside className="sidebar" id="sidebar" aria-label="Navegação">
      <a className="brand" href="#/visao-geral" aria-label="Jarvis Master — Visão Geral">
        <Icon name="logo" className="brand-mark" />
        <span><span className="brand-name">Jarvis</span><span className="brand-sub">Master</span></span>
      </a>
      <nav className="nav" aria-label="Principal">
        {items.filter((v) => !v.bottom).map(link)}
        <div className="nav-sep" role="separator" />
        {items.filter((v) => v.bottom).map(link)}
      </nav>
      <div className="sidebar-foot">
        <span className="conn-line" title="Servidor (API) do Jarvis">
          <span className={`dot ${api.state === 'up' ? 'on' : 'off'}`} />
          {api.state === 'checking' ? 'Verificando servidor…' : api.state === 'up' ? 'Servidor do Jarvis no ar' : 'Servidor do Jarvis desligado'}
        </span>
        <span className="conn-line"><span className={`dot ${conn?.database ? 'on' : 'off'}`} />{conn?.database ? `Banco de dados ativo${api.state === 'up' && api.health.database.kind === 'pglite' ? ' (local)' : ''}` : 'Banco de dados indisponível'}</span>
        <span className="conn-line"><span className={`dot ${conn?.auth ? 'on' : 'off'}`} />{conn?.auth ? 'Login com 2 fatores ativo' : 'Sem login (modo local)'}</span>
        <span className="conn-line"><span className={`dot ${gmailOn ? 'on' : 'off'}`} />{gmailOn ? 'Gmail conectado (leitura)' : gs?.needsReconnect ? 'Gmail pede reconexão' : 'Gmail não conectado'}</span>
        <span className="conn-line"><span className={`dot ${conn?.ai ? 'on' : 'off'}`} />{conn?.ai ? 'Modelo de IA configurado' : 'Nenhum modelo de IA'}</span>
        <span className="mono">v{APP_VERSION} · Etapa {APP_STAGE}</span>
        {/* Carimbo da compilação: diz na hora se o navegador está com uma versão antiga. */}
        <span className="mono build-stamp" title="Data em que esta versão do app foi compilada">build {__BUILD__} UTC</span>
      </div>
    </aside>
  );
}

function Topbar({ title, onMenu, navOpen }: { title: string; onMenu: () => void; navOpen: boolean }) {
  const { stateMode } = usePrefs();
  const dark = useIsDark();
  const themeLabel = dark ? 'Usar tema claro' : 'Usar tema escuro';
  return (
    <header className="topbar">
      <button type="button" className="btn btn-ghost btn-icon menu-btn" aria-label="Abrir menu" aria-controls="sidebar" aria-expanded={navOpen} onClick={onMenu}><Icon name="menu" /></button>
      <span className="topbar-title">{title}</span>
      {dark && <Clock />}
      <label className="state-picker" title="Laboratório de estados: como as telas carregam os dados">
        <Icon name="flask" /><span className="label-text">Estado</span>
        <select aria-label="Modo de estado da interface" value={stateMode} onChange={(e) => prefsStore.set({ stateMode: e.target.value as StateMode })}>
          {STATE_MODES.map((m) => <option key={m} value={m}>{STATE_MODE_LABEL[m]}</option>)}
        </select>
      </label>
      <button type="button" className="btn btn-ghost btn-icon" aria-label={themeLabel} title={themeLabel}
        onClick={() => prefsStore.set({ theme: dark ? 'light' : 'dark' })}><Icon name={dark ? 'sun' : 'moon'} /></button>
    </header>
  );
}

function DemoBanner() {
  const { stateMode } = usePrefs();
  if (stateMode !== 'demo') return null;
  return (
    <div className="demo-banner" role="note">
      <strong>Demonstração</strong>
      <span>Dados fictícios. Nenhuma conta, serviço externo ou modelo de IA está conectado.</span>
      <a href="#/configuracoes/laboratorio">Trocar estado</a>
    </div>
  );
}

export function Shell({ items, current, title, children }: { items: NavItem[]; current: string; title: string; children: ReactNode }) {
  const [navOpen, setNavOpen] = useState(false);
  /* No tema escuro, as telas internas usam o mesmo HUD da tela inicial. */
  const dark = useIsDark();

  useEffect(() => { setNavOpen(false); }, [current]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <a className="skip" href="#main" onClick={(e) => { e.preventDefault(); document.getElementById('main')?.focus(); }}>Pular para o conteúdo</a>
      <Ambient hud={dark} />
      {dark && <><Backdrop /><div className="app-frame" aria-hidden="true"><Frame /></div></>}
      <div className={`app ${dark ? 'hud' : ''} ${navOpen ? 'nav-open' : ''}`}>
        <Sidebar items={items} current={current} />
        <div className="scrim" onClick={() => setNavOpen(false)} />
        <div className="main-col">
          <DemoBanner />
          <Topbar title={title} navOpen={navOpen} onMenu={() => setNavOpen((o) => !o)} />
          <main id="main" tabIndex={-1}>{children}</main>
        </div>
      </div>
    </>
  );
}
